import { useCallback, useEffect, useRef, useState } from "react";
import { chatApiInstance } from "../apis/chatApi";

const loadThread = async (id: string) => {
  const response: any = await chatApiInstance.getAiThreadById(id);
  if (!response?.ai_thread?.ai_thread_id) {
    throw new Error(response?.error || "Unable to load messages");
  }
  return response.ai_thread;
};

type Entry = { promise: Promise<any>; thread?: any };

/** History loading is separate from turn activity: it must never enable Stop. */
export default function useThreadHistory(
  threadId: string | null | undefined,
  userId: string,
  fetchThread = loadThread
) {
  const cache = useRef(new Map<string, Entry>());
  const [result, setResult] = useState<{ key: string; thread?: any; error?: string }>({ key: "" });
  const [attempt, setAttempt] = useState(0);
  const keyFor = useCallback((id: string) => `${userId.toLowerCase()}::${id}`, [userId]);
  const key = userId && threadId ? keyFor(String(threadId)) : "";

  const request = useCallback((id: string) => {
    const requestKey = keyFor(id);
    const cached = cache.current.get(requestKey);
    if (cached) return cached.promise;
    const entry: Entry = {
      promise: Promise.resolve().then(() => fetchThread(id)).then(thread => {
        entry.thread = thread;
        return thread;
      }).catch(error => {
        cache.current.delete(requestKey);
        throw error;
      }),
    };
    cache.current.set(requestKey, entry);
    return entry.promise;
  }, [fetchThread, keyFor]);

  useEffect(() => {
    if (!key || !threadId) return;
    let current = true;
    setResult({ key });
    request(String(threadId)).then(
      thread => { if (current) setResult({ key, thread }); },
      error => { if (current) setResult({ key, error: error?.message || "Unable to load messages" }); }
    );
    return () => { current = false; };
  }, [key, threadId, request, attempt]);

  const seed = useCallback((id: string, thread: any) => {
    cache.current.set(keyFor(id), { promise: Promise.resolve(thread), thread });
  }, [keyFor]);
  const retry = useCallback(() => setAttempt(value => value + 1), []);
  const cached = key ? cache.current.get(key)?.thread : undefined;
  const error = result.key === key ? result.error : undefined;
  return {
    thread: cached || (result.key === key ? result.thread : undefined),
    isLoading: Boolean(key && !cached && !error),
    error,
    seed,
    retry,
  };
}
